import { useState, useMemo, useEffect } from "react";
import { createBoltDocumentTemplateApiClient } from "../../lib/apiClient/boltDocumentTemplateApiClient";
import { BoltDocumentTemplateFormProps } from "./BoltDocumentTemplateFormCreate";
import { BoltApiClient } from "@bolt/core";
import { BoltDocumentTemplate } from "../../types/types";
import { TextFieldValue, FileFieldValue, AppToasterV2 } from "@ucl/library";

export const useBoltDocumentTemplateForm = (
  props: BoltDocumentTemplateFormProps,
  boltApiClient?: BoltApiClient,
  templateId?: string
) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateFile, setTemplateFile] = useState<BoltDocumentTemplate>();

  const boltDocumentTemplateApiClient = useMemo(
    () => createBoltDocumentTemplateApiClient(boltApiClient!),
    [boltApiClient]
  );

  useEffect(() => {
    if (templateId) {
      getTemplate(templateId);
    }
  }, [templateId]);

  const getTemplate = async (templateId: string) => {
    setIsLoading(true);
    const template =
      await boltDocumentTemplateApiClient.getTemplate(templateId);
    setTemplateName(template.templateName);
    setIsLoading(false);
  };

  const updateTemplate = async () => {
    if (!templateId) {
      return;
    }

    setIsSaving(true);
    await boltDocumentTemplateApiClient
      .updateTemplate(templateId, templateName)
      .then((_response) => {
        AppToasterV2.showSuccessAppToaster("Template updated successfully");
        props.onSave?.();
      })
      .catch((error) => {
        console.log("error", error);
        AppToasterV2.showFailedActionToaster("Failed to update template");
      })
      .finally(() => {
        setIsSaving(false);
      });

    setIsSaving(false);
  };

  const deleteTemplate = async () => {
    if (!templateId) {
      return;
    }

    setIsSaving(true);
    await boltDocumentTemplateApiClient.deleteTemplate(templateId);
    setIsSaving(false);
  };

  // Memoize TextField props
  const textFieldProps = useMemo(
    () => ({
      label: "Template Name",
      isRequired: true,
      onSubmit: (value: TextFieldValue) => {
        setTemplateName(value ?? "");
      },
    }),
    [setTemplateName]
  );

  // Memoize FileField props
  const fileFieldProps = useMemo(
    () => ({
      type: "File" as const,
      label: "Template File",
      isRequired: templateId ? false : true,
      documentsFolderName: "boltdocumenttemplates",
      documentId: templateId,
      value: undefined,
      uploaderConfig: {
        allowedExtensions: ".pdf",
        multiple: false,
        files: undefined,
        asyncSettings: {
          chunkSize: 102400,
          saveUrl: `${boltApiClient?.baseApiUrl}/document-templates/upload`,
          removeUrl: `${boltApiClient?.baseApiUrl}/document-templates/remove`,
        },
      },
      onCompleteUploading: () => {
        console.log("Complete Uploading");
      },
      onSubmit: (value: FileFieldValue) => {
        setTemplateFile({ ...(value as unknown as BoltDocumentTemplate) });
      },
    }),
    [boltApiClient?.baseApiUrl, setTemplateFile, templateId]
  );

  const createTemplate = async () => {
    if (!templateFile) {
      return;
    }

    setIsSaving(true);
    await boltDocumentTemplateApiClient
      .createTemplate(templateFile.id, templateName)
      .then((_response) => {
        AppToasterV2.showSuccessAppToaster("Template created successfully");
        props.onSave?.();
      })
      .catch((_error) => {
        AppToasterV2.showFailedActionToaster("Failed to create template");
      })
      .finally(() => {
        setIsSaving(false);
      });

    setIsSaving(false);
  };

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isSaving,
    isLoading,
    templateName,
    setTemplateName,
    templateFile,
    setTemplateFile,
    textFieldProps,
    fileFieldProps,
  };
};
